// PO-3661: transient "artwork still processing" retry policy for DTF
// Add-to-Cart. When the request races ahead of the async image-enhancement
// pipeline, the server returns HTTP 409 with
// `{error: 'artwork_processing', retryable: true, retry_after_ms}` instead of
// the misleading terminal 422. The client retries transparently a bounded
// number of times so the soon-to-be-resolved error is never shown to the user.
import { tryParse } from "common/utils/json";

export const ARTWORK_PROCESSING_MAX_RETRIES = 4;
export const ARTWORK_PROCESSING_DEFAULT_RETRY_MS = 1500;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" ? (value as Record<string, unknown>) : null;

// Resolve the structured error body from a thrown `request.js` error. That
// layer passes raw JSON through `error.message` only for 422; every other
// status (including the 409 still-processing race) arrives as
// "<StatusText>: <json>" with the parsed JSON on `error.response`. Prefer the
// structured object so retry detection works regardless of status.
export const resolveAddToCartErrorBody = (error: unknown): unknown => {
  const response = asRecord((error as { response?: unknown })?.response);
  if (response) return response;

  const message = (error as { message?: unknown })?.message;
  return typeof message === "string" ? tryParse(message) : null;
};

export const isArtworkProcessingRetryable = (errorResponse: unknown): boolean => {
  const record = asRecord(errorResponse);
  return record?.["error"] === "artwork_processing" && record?.["retryable"] === true;
};

export const artworkProcessingRetryDelayMs = (errorResponse: unknown): number => {
  // The server always sends `retry_after_ms` on the 409, so the default is a
  // safety net for a missing/invalid field (contract drift, a future flag
  // gating the response). It is intentionally the same 1.5s the server uses, so
  // the fallback degrades gracefully rather than retrying too fast/slow.
  const ms = asRecord(errorResponse)?.["retry_after_ms"];
  return typeof ms === "number" && ms > 0 ? ms : ARTWORK_PROCESSING_DEFAULT_RETRY_MS;
};

// PO-3661: the retry-scheduling decision for `submitAddToCart`, extracted so
// the loop's core behavior (the `attempt < MAX` gate + the retryable-body check
// + the delay) is unit-testable without the MobX store's mock surface. Returns
// true when a transparent retry was scheduled (the caller must then keep its
// submit lock held and return); false when the error is terminal or the retry
// budget is exhausted (the caller surfaces the error). `schedule` is injectable
// so tests can drive it without real timers.
export const scheduleProcessingRetry = (
  errorResponse: unknown,
  attempt: number,
  schedule: (delayMs: number) => void
): boolean => {
  if (!isArtworkProcessingRetryable(errorResponse) || attempt >= ARTWORK_PROCESSING_MAX_RETRIES) {
    return false;
  }

  schedule(artworkProcessingRetryDelayMs(errorResponse));
  return true;
};
